import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useSdk } from '@fondationcss/sdk';
import { useSdk as useCustomSdk } from '@/composables/use-sdk';
import type { InitializationStepStatus } from '@/sdk/model/initialization-status';

export interface WizardStep {
  key: string;
  title: string;
  description: string;
  icon: string;
  route: string;
  completed: boolean;
  optional: boolean;
}

const STEP_DEFINITIONS: Omit<WizardStep, 'completed'>[] = [
  {
    key: 'configure-privacy',
    title: 'Configurer les paramètres de confidentialité',
    description: 'Renseigner le responsable de la collecte, le RPRP et son courriel.',
    icon: 'fas fa-shield-halved',
    route: '/gestion/confidentialite/parametres',
    optional: true,
  },
  {
    key: 'define-sites',
    title: 'Définir les centres',
    description: 'Créer au moins un centre de formation.',
    icon: 'fas fa-building',
    route: '/gestion/centres',
    optional: false,
  },
  {
    key: 'define-locals',
    title: 'Définir les locaux',
    description: 'Créer au moins un local pour accueillir les formations.',
    icon: 'fas fa-door-open',
    route: '/gestion/locaux-ressources',
    optional: false,
  },
  {
    key: 'define-resources',
    title: 'Définir les ressources',
    description: 'Créer et associer au moins une ressource à un local.',
    icon: 'fas fa-desktop',
    route: '/gestion/locaux-ressources',
    optional: true,
  },
  {
    key: 'define-calendars',
    title: 'Définir les calendriers',
    description: 'Créer au moins un calendrier (années, congés).',
    icon: 'fas fa-calendar',
    route: '/core/calendar',
    optional: false,
  },
  {
    key: 'define-schedule-patterns',
    title: "Définir les patrons d'horaire",
    description: "Créer au moins un patron d'horaire (périodes de la journée).",
    icon: 'fas fa-clock',
    route: '/core/schedule-patterns',
    optional: false,
  },
  {
    key: 'activate-programs',
    title: 'Activer les programmes',
    description: 'Activer au moins un programme de formation.',
    icon: 'fas fa-book-open',
    route: '/gestion/programmes',
    optional: false,
  },
  {
    key: 'sequence-modules',
    title: 'Séquencer les compétences',
    description: 'Définir les prérequis pour au moins une compétence d\'un programme actif.',
    icon: 'fas fa-sort-amount-down',
    route: '/gestion/programmes',
    optional: false,
  },
  {
    key: 'assign-module-resources',
    title: 'Assigner ressources/locaux aux compétences',
    description: 'Associer des ressources ou locaux aux compétences d\'un programme actif.',
    icon: 'fas fa-link',
    route: '/gestion/programmes',
    optional: true,
  },
  {
    key: 'create-teachers',
    title: 'Créer des enseignants',
    description: 'Ajouter au moins un enseignant dans le système.',
    icon: 'fas fa-chalkboard-teacher',
    route: '/gestion/enseignants',
    optional: false,
  },
  {
    key: 'assign-teacher-modules',
    title: 'Assigner programmes et compétences aux enseignants',
    description: 'Assigner au moins une compétence à un enseignant.',
    icon: 'fas fa-user-tag',
    route: '/gestion/enseignants',
    optional: false,
  },
  {
    key: 'plan-group',
    title: 'Planifier et confirmer un groupe',
    description: 'Créer au moins un groupe et le confirmer.',
    icon: 'fas fa-users',
    route: '/planification/vue-ensemble',
    optional: true,
  },
  {
    key: 'add-frequentation',
    title: 'Ajouter des élèves et fréquentations',
    description: 'Créer au moins un élève et une fréquentation dans le système.',
    icon: 'fas fa-user-graduate',
    route: '/planification/etudiants',
    optional: false,
  },
  {
    key: 'plan-individualized',
    title: 'Planifier un parcours individualisé',
    description: 'Créer au moins une fréquentation individualisée.',
    icon: 'fas fa-user-friends',
    route: '/planification/individualise',
    optional: true,
  },
  {
    key: 'record-presences',
    title: 'Prendre des présences',
    description: 'Enregistrer au moins une présence dans le système.',
    icon: 'fas fa-calendar-check',
    route: '/enseignement/presences',
    optional: false,
  },
];

const DISMISSED_STORAGE_KEY = 'parcours.wizard.dismissed';

const showStepsDialog = ref(false);
const dismissed = ref(readDismissedFlag());

function readDismissedFlag(): boolean {
  try {
    return localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function dismissWizard() {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
  } catch {
    // Ignore storage errors — dismissal is a convenience, not critical
  }
  dismissed.value = true;
  showStepsDialog.value = false;
}

export function resetWizardDismissal() {
  try {
    localStorage.removeItem(DISMISSED_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
  dismissed.value = false;
  showStepsDialog.value = true;
}

const ADMIN_PERMISSION = 'Parcours.LearningModule.Administrateur';

/**
 * Who the assistant is for. Configuring the application is administrator work — every step it
 * lists lands on a `/gestion` or `/core` screen — so anyone else has nothing to do with it.
 *
 * Exported on its own so a surface that only needs to decide whether to OFFER the assistant
 * (the profile menu) can ask without instantiating the whole composable, which would fire a
 * second status fetch on every mount.
 */
export function useWizardAudience() {
  const coreSdk = useSdk();
  return computed(() => (coreSdk.me.value?.permissions || []).includes(ADMIN_PERMISSION));
}

export function useInitializationWizard() {
  const customSdk = useCustomSdk();
  const router = useRouter();

  const steps = ref<WizardStep[]>([]);
  const loading = ref(false);
  const loaded = ref(false);
  const showWelcomeDialog = ref(false);

  const isAdmin = useWizardAudience();

  const completedCount = computed(() => steps.value.filter(s => s.completed).length);
  const requiredCount = computed(() => steps.value.filter(s => !s.optional).length);
  const requiredCompletedCount = computed(() => steps.value.filter(s => !s.optional && s.completed).length);
  const totalCount = computed(() => steps.value.length);
  const allCompleted = computed(() => requiredCompletedCount.value === requiredCount.value);
  const anyCompleted = computed(() => completedCount.value > 0);
  const showFab = computed(() => isAdmin.value && loaded.value && !dismissed.value);

  function mergeStatus(statusSteps: InitializationStepStatus[]) {
    const statusMap = new Map(statusSteps.map(s => [s.key, s]));
    steps.value = STEP_DEFINITIONS.map(def => ({
      ...def,
      completed: statusMap.get(def.key)?.completed ?? false,
    }));
  }

  async function loadStatus(options?: {
    markLoaded?: boolean;
    showWelcomeWhenEmpty?: boolean;
  }) {
    if (!isAdmin.value) return;
    loading.value = true;
    try {
      const response = await customSdk.getInitializationStatus();
      mergeStatus(response.steps);

      if (options?.markLoaded) {
        loaded.value = true;
      }

      // Show welcome dialog only if no step has been completed yet
      if (options?.showWelcomeWhenEmpty && !anyCompleted.value) {
        showWelcomeDialog.value = true;
      }
    } catch {
      // Silently fail — wizard is non-critical
    } finally {
      loading.value = false;
    }
  }

  async function fetchStatus() {
    await loadStatus({ markLoaded: true, showWelcomeWhenEmpty: true });
  }

  async function refreshStatus() {
    await loadStatus();
  }

  function openStepsDialog() {
    showStepsDialog.value = true;
    refreshStatus();
  }

  function closeStepsDialog() {
    showStepsDialog.value = false;
  }

  function startConfiguration() {
    showWelcomeDialog.value = false;
    openStepsDialog();
  }

  function navigateToStep(step: WizardStep) {
    showStepsDialog.value = false;
    router.push(step.route);
  }

  // Watch for isAdmin to become true (sdk.me loads asynchronously after auth)
  watch(isAdmin, (admin) => {
    if (admin && !loaded.value) {
      fetchStatus();
    }
  }, { immediate: true });

  return {
    steps,
    loading,
    loaded,
    isAdmin,
    completedCount,
    requiredCount,
    requiredCompletedCount,
    totalCount,
    allCompleted,
    anyCompleted,
    showFab,
    showWelcomeDialog,
    showStepsDialog,
    dismissed,
    openStepsDialog,
    closeStepsDialog,
    startConfiguration,
    navigateToStep,
    refreshStatus,
    dismissWizard,
  };
}
